import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress, Container,
  Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton,
  InputAdornment, List, ListItem, ListItemIcon, ListItemText, Menu, MenuItem,
  Switch, TextField, Typography
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import CloseIcon from '@material-ui/icons/Close'
import DeleteIcon from '@material-ui/icons/Delete'
import SearchIcon from '@material-ui/icons/Search'
import AddIcon from '@material-ui/icons/Add'

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
// Sell figure to display/roll-up: the pricing engine's totalSell when set,
// otherwise a per-line total entered manually or imported (importedTotal).
const effectiveTotal = (li) => (li && li.totalSell > 0 ? li.totalSell : (li && li.importedTotal) || 0)
const isImported = (li) => li && !(li.totalSell > 0) && li.importedTotal > 0

/* ── new-invoice project picker (creates a draft) ──────────────── */
const NewInvoiceDialog = ({ url, open, onClose, onCreated }) => {
  const [projects, setProjects] = useState(null)
  const [q, setQ] = useState('')
  const [creating, setCreating] = useState(false)
  useEffect(() => {
    if (!open) return
    axios.get(`${url}/api/bid-estimation/projects`)
      .then(res => setProjects(res.data || []))
      .catch(() => setProjects([]))
  }, [url, open])
  const filtered = useMemo(() => {
    if (!projects) return []
    const needle = q.trim().toLowerCase()
    return (needle ? projects.filter(p => (p.name || '').toLowerCase().includes(needle)) : projects).slice(0, 20)
  }, [projects, q])
  const choose = (p) => {
    setCreating(true)
    axios.post(`${url}/api/bid-estimation/invoice-grouping/drafts`, { projectId: p._id })
      .then(res => onCreated(res.data))
      .catch(e => window.alert(e.response?.data?.error || e.message))
      .then(() => setCreating(false))
  }
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New Invoice — choose a bid project</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth variant="outlined" placeholder="Search bid projects…" value={q}
          onChange={e => setQ(e.target.value)} autoFocus disabled={creating}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
        />
        {projects === null || creating ? <Box mt={3} textAlign="center"><CircularProgress /></Box> : (
          <List>
            {filtered.map(p => (
              <ListItem button key={p._id} onClick={() => choose(p)}>
                <ListItemText primary={p.name} secondary={`${p.status || ''} · updated ${new Date(p.updatedAt).toLocaleDateString()}`} />
              </ListItem>
            ))}
            {!filtered.length && <Typography variant="body2" color="textSecondary" align="center">No matching projects.</Typography>}
          </List>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Cancel</Button></DialogActions>
    </Dialog>
  )
}

/* ── drafts landing: every invoice draft across all projects ───── */
const DraftList = ({ url, onOpen }) => {
  const [drafts, setDrafts] = useState(null)
  const [newOpen, setNewOpen] = useState(false)
  const base = `${url}/api/bid-estimation/invoice-grouping`
  const load = useCallback(() => {
    axios.get(`${base}/drafts`).then(res => setDrafts(res.data || [])).catch(() => setDrafts([]))
  }, [base])
  useEffect(() => { load() }, [load])

  const deleteDraft = (d, e) => {
    e.stopPropagation()
    if (!window.confirm(`Delete invoice draft "${d.name}"? Its groups are removed (line items and prices are unaffected).`)) return
    axios.delete(`${base}/drafts/${d._id}`).then(load).catch(err => window.alert(err.response?.data?.error || err.message))
  }

  return (
    <Box maxWidth={760} mx="auto" mt={5}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="h5">Invoice Drafts</Typography>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setNewOpen(true)}>
          New Invoice
        </Button>
      </Box>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Each draft is an independent working copy — group a project’s line items into invoices. Multiple drafts can run on the same project.
      </Typography>
      {drafts === null ? <Box mt={3} textAlign="center"><CircularProgress /></Box> : (
        <List>
          {drafts.map(d => (
            <ListItem button divider key={d._id} onClick={() => onOpen(d._id)}>
              <ListItemText
                primary={d.name}
                secondary={
                  `${d.projectName} · ${d.groupCount || 0} group${d.groupCount === 1 ? '' : 's'}` +
                  `${d.readyCount ? ` · ${d.readyCount} ready` : ''}` +
                  `${d.createdByName ? ` · ${d.createdByName}` : ''} · updated ${new Date(d.updatedAt).toLocaleDateString()}`
                }
              />
              {d.readyCount > 0 && <Chip size="small" label={`${d.readyCount} ready`} style={{ backgroundColor: '#4caf50', color: '#fff', marginRight: 8 }} />}
              <IconButton size="small" onClick={e => deleteDraft(d, e)}><DeleteIcon fontSize="small" /></IconButton>
            </ListItem>
          ))}
          {!drafts.length && (
            <Typography variant="body2" color="textSecondary" align="center" style={{ padding: 24 }}>
              No invoice drafts yet. Click “New Invoice” to start one from a bid project.
            </Typography>
          )}
        </List>
      )}
      <NewInvoiceDialog url={url} open={newOpen} onClose={() => setNewOpen(false)} onCreated={d => { setNewOpen(false); onOpen(d._id) }} />
    </Box>
  )
}

/* ── Aspire opportunity picker (one invoice → one/many jobs) ───── */
const oppLabel = (o) =>
  `#${o.OpportunityNumber || o.OpportunityID} — ${o.OpportunityName || ''}${o.PropertyName ? ` (${o.PropertyName.trim()})` : ''}`

const OpportunityPicker = ({ url, value, onChange }) => {
  const [input, setInput] = useState('')
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const q = input.trim()
    if (q.length < 2) { setOptions([]); return }
    let active = true
    setLoading(true)
    const t = setTimeout(() => {
      axios.get(`${url}/api/bid-estimation/invoice-grouping/opportunities/search`, { params: { q } })
        .then(res => { if (active) setOptions(res.data || []) })
        .catch(() => { if (active) setOptions([]) })
        .then(() => { if (active) setLoading(false) })
    }, 250)
    return () => { active = false; clearTimeout(t) }
  }, [input, url])

  return (
    <Autocomplete
      multiple size="small" fullWidth
      inputValue={input}
      value={value || []}
      options={options}
      loading={loading}
      filterSelectedOptions
      getOptionLabel={oppLabel}
      getOptionSelected={(a, b) => a.OpportunityID === b.OpportunityID}
      filterOptions={x => x}  // server-side search; don't re-filter locally
      onInputChange={(e, v, reason) => { if (reason !== 'reset') setInput(v) }}
      onChange={(e, v) => { onChange(v); setInput('') }}
      renderInput={params => (
        <TextField
          {...params} variant="outlined" label="Aspire jobs (opportunities)"
          placeholder="Search by opportunity #, name, or property…"
          InputProps={{
            ...params.InputProps,
            endAdornment: <>{loading ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>
          }}
        />
      )}
      renderTags={(vals, getTagProps) =>
        vals.map((o, i) => (
          <Chip size="small" key={o.OpportunityID} label={`#${o.OpportunityNumber || o.OpportunityID}`} {...getTagProps({ index: i })} />
        ))
      }
    />
  )
}

/* ── per-crew % attribution of visits across invoice groups ────── */
// savedAllocations: [{ routeId, splits: [{ groupId, percent }] }]
// Local edit state: { [routeId]: { [groupId]: percentString } }
// Generic % matrix: rows × invoice groups, each row totals 100% (warned).
// rows: [{ key, primary, secondary }]. rowColLabel: header for the row column.
// heading: table title. savedByKey: { [rowKey]: { [groupId]: percent } }.
// buildAllocation(row, splits): shape one saved allocation object.
const AllocationMatrix = ({ rows, groups, rowColLabel, heading, savedByKey, buildAllocation, onSave, emptyRowsText, rowAction }) => {
  const seed = useMemo(() => {
    const m = {}
    for (const k of Object.keys(savedByKey || {})) {
      m[k] = {}
      for (const gid of Object.keys(savedByKey[k])) m[k][gid] = String(savedByKey[k][gid])
    }
    return m
  }, [savedByKey])
  const [edits, setEdits] = useState(seed)
  useEffect(() => { setEdits(seed) }, [seed])

  if (!groups.length) {
    return <Typography variant="body2" color="textSecondary">Create invoice groups first, then attribute to them.</Typography>
  }
  if (!rows.length) {
    return <Typography variant="body2" color="textSecondary">{emptyRowsText || 'Nothing to attribute.'}</Typography>
  }

  const cellVal = (rowKey, groupId) => (edits[rowKey] && edits[rowKey][groupId]) || ''
  const rowTotal = (rowKey) => groups.reduce((s, g) => s + (Number(cellVal(rowKey, g._id)) || 0), 0)
  const setCell = (rowKey, groupId, v) =>
    setEdits(e => ({ ...e, [rowKey]: { ...(e[rowKey] || {}), [groupId]: v } }))

  // Persist the whole matrix — the API replaces allocations wholesale.
  const persist = () => {
    const allocations = rows.map(r => {
      const splits = groups
        .map(g => ({ groupId: g._id, percent: Number(cellVal(r.key, g._id)) || 0 }))
        .filter(s => s.percent > 0)
      return buildAllocation(r, splits)
    }).filter(a => a.splits.length)
    onSave(allocations)
  }

  const stickyTh = { padding: '6px 8px', fontWeight: 600, position: 'sticky', top: 0, background: '#fff', zIndex: 1, boxShadow: 'inset 0 -1px 0 #ddd' }
  return (
    <Box mt={2}>
      <Typography variant="subtitle2" gutterBottom>{heading}</Typography>
      <Box style={{ overflow: 'auto', maxHeight: 420 }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 480 }}>
        <thead>
          <tr>
            <th style={{ ...stickyTh, textAlign: 'left' }}>{rowColLabel}</th>
            {groups.map(g => (
              <th key={g._id} style={{ ...stickyTh, whiteSpace: 'nowrap' }}>{g.name}</th>
            ))}
            <th style={stickyTh}>Total</th>
            {rowAction && <th style={stickyTh} />}
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const total = rowTotal(r.key)
            const ok = Math.round(total) === 100
            return (
              <tr key={r.key} style={{ borderTop: '1px solid #eee' }}>
                <td style={{ padding: '4px 8px' }}>
                  <Typography variant="body2">{r.primary}</Typography>
                  {r.secondary && <Typography variant="caption" color="textSecondary">{r.secondary}</Typography>}
                </td>
                {groups.map(g => (
                  <td key={g._id} style={{ padding: '4px 8px', textAlign: 'center' }}>
                    <TextField
                      size="small" type="number" variant="outlined"
                      value={cellVal(r.key, g._id)}
                      onChange={e => setCell(r.key, g._id, e.target.value)}
                      onBlur={persist}
                      inputProps={{ min: 0, max: 100, style: { width: 52, textAlign: 'right', padding: '6px 8px' } }}
                    />
                  </td>
                ))}
                <td style={{ padding: '4px 8px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                  <Typography variant="body2" style={{ color: ok ? '#4caf50' : '#d32f2f', fontWeight: 600 }}>
                    {total}%{ok ? '' : ' ≠100'}
                  </Typography>
                </td>
                {rowAction && <td style={{ padding: '4px 8px', textAlign: 'center' }}>{rowAction(r)}</td>}
              </tr>
            )
          })}
        </tbody>
      </table>
      </Box>
    </Box>
  )
}

/* ── work ticket visits for the draft's linked opportunities ───── */
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-US') : '—')

// Deep links into the Aspire web app so users can inspect the source record.
const ASPIRE_APP = 'https://cloud.youraspire.com/app'
const aspireLink = {
  receipt: (id) => `${ASPIRE_APP}/purchasing/receipts/details/${id}`,
  workTicket: (id) => `${ASPIRE_APP}/worktickets/details/${id}`,
  visit: (id) => `${ASPIRE_APP}/worktickets/visit/${id}`
}
// Small "↗ Aspire" external link, or nothing when the id is missing.
const AspireLink = ({ href, label }) => href
  ? <a href={href} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, marginLeft: 6 }}>{label} ↗</a>
  : null

const VisitsPanel = ({ url, draftId, reloadKey, hasOpportunities, groups, savedAllocations, onSaveAllocations, onCrewsLoaded, hoursOverride, onSaveHoursOverride, crewRates, onSaveCrewRates }) => {
  const [visits, setVisits] = useState(null)
  const [crews, setCrews] = useState([])
  const [error, setError] = useState(null)
  const [showVisits, setShowVisits] = useState(false)
  const laborTotal = crews.reduce((s, c) => s + (Number(c.laborCost) || 0), 0)

  // local edits for per-crew rate, seeded from saved crewRates
  const savedRateByRoute = useMemo(() =>
    (crewRates || []).reduce((m, r) => { m[r.routeId] = r.rate; return m }, {}), [crewRates])
  const [rateEdits, setRateEdits] = useState({})
  useEffect(() => { setRateEdits(savedRateByRoute) }, [savedRateByRoute])
  const persistRates = () => {
    const next = crews.map(c => ({ routeId: c.routeId, rate: Number(rateEdits[c.routeId]) || 0 }))
      .filter(r => r.rate > 0)
    onSaveCrewRates(next)
  }
  useEffect(() => {
    if (!hasOpportunities) { setVisits([]); setCrews([]); onCrewsLoaded && onCrewsLoaded([]); return }
    let active = true
    setVisits(null); setError(null)
    axios.get(`${url}/api/bid-estimation/invoice-grouping/drafts/${draftId}/visits`)
      .then(res => { if (active) { setVisits(res.data.visits || []); const cc = res.data.crews || []; setCrews(cc); onCrewsLoaded && onCrewsLoaded(cc) } })
      .catch(e => { if (active) setError(e.response?.data?.error || e.message) })
    return () => { active = false }
  }, [url, draftId, reloadKey, hasOpportunities, onCrewsLoaded])

  if (!hasOpportunities) return null
  const seeded = Number.isFinite(hoursOverride) && hoursOverride > 0
  return (
    <Card style={{ marginBottom: 16 }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" style={{ gap: 8 }}>
          <Typography variant="h6">
            Work Ticket Visits{visits ? ` (${visits.length})` : ''}
            {visits && !!visits.length && <span style={{ color: '#6a1b9a', fontWeight: 600, marginLeft: 8 }}>· Labor {money(laborTotal)}</span>}
          </Typography>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            {seeded
              ? <>
                  <Chip size="small" label={`Seeded ${hoursOverride}h / entry`} style={{ backgroundColor: '#6a1b9a', color: '#fff' }} />
                  <Button size="small" onClick={() => onSaveHoursOverride(null)}>Clear</Button>
                </>
              : <Button size="small" variant="outlined" onClick={() => onSaveHoursOverride(8)}>Seed 8h (test)</Button>}
          </Box>
        </Box>
        {seeded && (
          <Typography variant="caption" color="textSecondary">
            Test seeding: every non-drive crew entry is treated as {hoursOverride}h (real hours are 0 in this tenant). Stored on this draft only.
          </Typography>
        )}
        {error && <Typography variant="body2" color="error">{error}</Typography>}
        {visits === null && <Box mt={1}><CircularProgress size={20} /></Box>}
        {visits && !visits.length && (
          <Typography variant="body2" color="textSecondary">No visits found for the linked opportunities.</Typography>
        )}
        {visits && !!visits.length && (
          <Box mt={2} style={{ overflowX: 'auto' }}>
            <Typography variant="subtitle2" gutterBottom>Crew labor rates ($/hr)</Typography>
            <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 480 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 600 }}>Crew (route)</th>
                  <th style={{ padding: '4px 8px', fontWeight: 600 }}>Hours</th>
                  <th style={{ padding: '4px 8px', fontWeight: 600 }}>Rate $/hr</th>
                  <th style={{ padding: '4px 8px', fontWeight: 600 }}>Labor</th>
                </tr>
              </thead>
              <tbody>
                {crews.map(c => (
                  <tr key={c.routeId} style={{ borderTop: '1px solid #eee' }}>
                    <td style={{ padding: '4px 8px' }}>{c.routeName || `Route ${c.routeId}`}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right' }}>{(c.laborHours || 0).toFixed(1)}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                      <TextField
                        size="small" type="number" variant="outlined"
                        placeholder="PayRate"
                        value={rateEdits[c.routeId] != null ? rateEdits[c.routeId] : ''}
                        onChange={e => setRateEdits(p => ({ ...p, [c.routeId]: e.target.value }))}
                        onBlur={persistRates}
                        inputProps={{ min: 0, step: '0.01', style: { width: 80, textAlign: 'right', padding: '6px 8px' } }}
                      />
                    </td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {money(c.laborCost)}
                      <Typography variant="caption" color="textSecondary" style={{ marginLeft: 6 }}>
                        {c.crewRate != null ? 'crew rate' : 'PayRate'}
                      </Typography>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Typography variant="caption" color="textSecondary">
              Set a rate to use it for that crew’s labor; leave blank to use Aspire pay rates.
            </Typography>
          </Box>
        )}
        {visits && !!visits.length && (
          <AllocationMatrix
            groups={groups}
            rowColLabel="Crew (route)"
            heading="Attribute each crew’s visits to invoice groups (%)"
            emptyRowsText="No crews to attribute."
            rows={crews.map(c => ({
              key: String(c.routeId),
              routeId: c.routeId, routeName: c.routeName,
              primary: c.routeName || `Route ${c.routeId}`,
              secondary: `${c.visitCount} visit${c.visitCount === 1 ? '' : 's'}`
            }))}
            savedByKey={(savedAllocations || []).reduce((m, a) => {
              m[String(a.routeId)] = (a.splits || []).reduce((mm, s) => { mm[String(s.groupId)] = s.percent; return mm }, {})
              return m
            }, {})}
            buildAllocation={(r, splits) => ({ routeId: r.routeId, routeName: r.routeName, splits })}
            onSave={onSaveAllocations}
          />
        )}
        {visits && !!visits.length && (
          <Box mt={2}>
            <Button size="small" onClick={() => setShowVisits(v => !v)}>
              {showVisits ? 'Hide' : 'Show'} {visits.length} visits
            </Button>
          </Box>
        )}
        {visits && !!visits.length && showVisits && (
          <List dense>
            {visits.slice(0, 100).map(v => (
              <ListItem key={v.WorkTicketVisitID || `${v.WorkTicketID}-${v.ScheduledDate}`} disableGutters>
                <ListItemText
                  primary={`WT #${v.WorkTicketNumber || v.WorkTicketID || '—'} · ${fmtDate(v.ScheduledDate)}`}
                  secondary={
                    [
                      v.RouteName,
                      v.WorkTicketStatusName,
                      v.actualCrewCount ? `${v.actualCrewCount} crew` : null,
                      v.ArrivalTime ? `in ${new Date(v.ArrivalTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}` : null
                    ].filter(Boolean).join(' · ') || `Opp #${v.OpportunityID}`
                  }
                />
                <AspireLink href={v.WorkTicketID ? aspireLink.workTicket(v.WorkTicketID) : null} label="WT" />
                <AspireLink href={v.WorkTicketVisitID ? aspireLink.visit(v.WorkTicketVisitID) : null} label="Visit" />
              </ListItem>
            ))}
            {visits.length > 100 && (
              <Typography variant="caption" color="textSecondary">Showing first 100 of {visits.length}.</Typography>
            )}
          </List>
        )}
      </CardContent>
    </Card>
  )
}

const money = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })

/* ── full-screen receipt review: PDF ‹ items + allocation, cycle ── */
const ReceiptReviewWindow = ({ url, draftId, receipts, startIndex, groups, savedByReceiptId, onSaveOne, onClose }) => {
  const [idx, setIdx] = useState(startIndex || 0)
  const [atts, setAtts] = useState(null)
  const [activeAtt, setActiveAtt] = useState(null)
  const [attError, setAttError] = useState(null)
  const base = `${url}/api/bid-estimation/invoice-grouping/drafts/${draftId}`
  const receipt = receipts[idx]

  // load this receipt's attachments whenever the index changes
  useEffect(() => {
    if (!receipt) return
    let active = true
    setAtts(null); setActiveAtt(null); setAttError(null)
    axios.get(`${base}/receipts/${receipt.ReceiptID}/attachments`)
      .then(res => { if (active) { const a = res.data.attachments || []; setAtts(a); setActiveAtt(a[0]?.AttachmentID || null) } })
      .catch(e => { if (active) setAttError(e.response?.data?.error || e.message) })
    return () => { active = false }
  }, [base, receipt])

  // local % edits for the current receipt, seeded from saved allocation
  const [pct, setPct] = useState({})
  useEffect(() => {
    setPct(receipt ? { ...(savedByReceiptId[receipt.ReceiptID] || {}) } : {})
  }, [receipt, savedByReceiptId])

  if (!receipt) return null
  const fileUrl = activeAtt ? `${base}/attachments/${activeAtt}/file` : null
  const activeObj = (atts || []).find(a => a.AttachmentID === activeAtt)
  const isImage = /\.(jpe?g|png|gif|webp|tif?f|bmp|heic)$/i.test(activeObj?.FileExtension || activeObj?.OriginalFileName || activeObj?.AttachmentName || '')
  const total = groups.reduce((s, g) => s + (Number(pct[g._id]) || 0), 0)
  const ok = Math.round(total) === 100

  const persist = () => {
    const splits = groups
      .map(g => ({ groupId: g._id, percent: Number(pct[g._id]) || 0 }))
      .filter(s => s.percent > 0)
    onSaveOne(receipt, splits)
  }
  const go = (delta) => { persist(); setIdx(i => Math.min(receipts.length - 1, Math.max(0, i + delta))) }

  return (
    <Dialog open fullScreen onClose={() => { persist(); onClose() }}>
      {/* top bar */}
      <Box display="flex" alignItems="center" px={2} py={1} style={{ gap: 12, borderBottom: '1px solid #ddd' }}>
        <Button onClick={() => { persist(); onClose() }}>✕ Close</Button>
        <Typography variant="h6" style={{ flexGrow: 1 }}>
          Receipt #{receipt.ReceiptNumber || receipt.ReceiptID} · {money(receipt.ReceiptTotalCost)}
        </Typography>
        <Typography variant="body2" color="textSecondary">{idx + 1} of {receipts.length}</Typography>
        <Button disabled={idx === 0} onClick={() => go(-1)}>← Prev</Button>
        <Button disabled={idx === receipts.length - 1} onClick={() => go(1)}>Next →</Button>
      </Box>

      <Box display="flex" style={{ height: 'calc(100vh - 57px)' }}>
        {/* left: PDF */}
        <Box style={{ flex: 3, minWidth: 0, borderRight: '1px solid #ddd', display: 'flex', flexDirection: 'column' }}>
          {attError && <Box p={2}><Typography color="error" variant="body2">{attError}</Typography></Box>}
          {atts === null && !attError && <Box textAlign="center" mt={4}><CircularProgress /></Box>}
          {atts && !atts.length && <Box p={2}><Typography variant="body2" color="textSecondary">No attachment on this receipt.</Typography></Box>}
          {atts && atts.length > 1 && (
            <Box p={1} display="flex" flexWrap="wrap" style={{ gap: 6 }}>
              {atts.map(a => (
                <Chip key={a.AttachmentID} size="small"
                  label={a.OriginalFileName || a.AttachmentName}
                  color={a.AttachmentID === activeAtt ? 'primary' : 'default'}
                  onClick={() => setActiveAtt(a.AttachmentID)} />
              ))}
            </Box>
          )}
          {fileUrl && isImage && (
            <Box style={{ flex: 1, overflow: 'auto', textAlign: 'center', background: '#f5f5f5' }}>
              <img src={fileUrl} alt="receipt" style={{ maxWidth: '100%', height: 'auto' }} />
            </Box>
          )}
          {fileUrl && !isImage && (
            <iframe title="receipt-file" src={fileUrl} style={{ flex: 1, width: '100%', border: 'none' }} />
          )}
        </Box>

        {/* right: details + line items + allocation */}
        <Box style={{ flex: 2, minWidth: 340, overflowY: 'auto', padding: 16 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2" color="textSecondary">Details</Typography>
            <AspireLink href={aspireLink.receipt(receipt.ReceiptID)} label="Receipt in Aspire" />
          </Box>
          <Typography variant="body2">
            {receipt.VendorName || `Vendor ${receipt.VendorID || '—'}`}
            {receipt.VendorInvoiceNum ? ` · inv ${receipt.VendorInvoiceNum}` : ''}
            {` · ${receipt.ReceiptStatusName || ''}`}
            {` · ${fmtDate(receipt.ReceivedDate)}`}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            WT {receipt.WorkTicketNumber || '—'}
            <AspireLink href={receipt.WorkTicketID ? aspireLink.workTicket(receipt.WorkTicketID) : null} label="Work ticket" />
          </Typography>
          {receipt.ReceiptNote && <Typography variant="caption" color="textSecondary">{receipt.ReceiptNote}</Typography>}

          <Box mt={2}>
            <Typography variant="subtitle2" color="textSecondary">Line items ({(receipt.ReceiptItems || []).length})</Typography>
            <List dense>
              {(receipt.ReceiptItems || []).map((it, i) => (
                <ListItem key={it.ReceiptItemID || i} disableGutters>
                  <ListItemText
                    primary={`${it.ItemName || '—'} · ${money(it.ItemExtendedCost != null ? it.ItemExtendedCost : (it.ItemQuantity || 0) * (it.ItemUnitCost || 0))}`}
                    secondary={`${it.ItemQuantity || 0} × ${money(it.ItemUnitCost)}${it.ItemType ? ` · ${it.ItemType}` : ''}`}
                  />
                </ListItem>
              ))}
              {!(receipt.ReceiptItems || []).length && <Typography variant="body2" color="textSecondary">No line items.</Typography>}
            </List>
          </Box>

          <Box mt={2}>
            <Typography variant="subtitle2" color="textSecondary">Attribute to invoice groups (%)</Typography>
            {!groups.length && <Typography variant="body2" color="textSecondary">Create invoice groups first.</Typography>}
            {groups.map(g => (
              <Box key={g._id} display="flex" alignItems="center" justifyContent="space-between" my={0.5}>
                <Typography variant="body2" style={{ flexGrow: 1 }}>{g.name}</Typography>
                <TextField
                  size="small" type="number" variant="outlined"
                  value={pct[g._id] != null ? pct[g._id] : ''}
                  onChange={e => setPct(p => ({ ...p, [g._id]: e.target.value }))}
                  onBlur={persist}
                  inputProps={{ min: 0, max: 100, style: { width: 60, textAlign: 'right', padding: '6px 8px' } }}
                />
              </Box>
            ))}
            {!!groups.length && (
              <Box mt={1} textAlign="right">
                <Typography variant="body2" style={{ color: ok ? '#4caf50' : '#d32f2f', fontWeight: 600 }}>
                  Total {total}%{ok ? '' : ' ≠100'}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Dialog>
  )
}

const ReceiptsPanel = ({ url, draftId, reloadKey, hasOpportunities, groups, savedAllocations, onSaveAllocations, onReceiptsLoaded }) => {
  const [receipts, setReceipts] = useState(null)
  const [error, setError] = useState(null)
  const [showList, setShowList] = useState(false)
  const [reviewIdx, setReviewIdx] = useState(null)   // index of receipt open in review window
  useEffect(() => {
    if (!hasOpportunities) { setReceipts([]); onReceiptsLoaded && onReceiptsLoaded([]); return }
    let active = true
    setReceipts(null); setError(null)
    axios.get(`${url}/api/bid-estimation/invoice-grouping/drafts/${draftId}/receipts`)
      .then(res => { if (active) { const rr = res.data.receipts || []; setReceipts(rr); onReceiptsLoaded && onReceiptsLoaded(rr) } })
      .catch(e => { if (active) setError(e.response?.data?.error || e.message) })
    return () => { active = false }
  }, [url, draftId, reloadKey, hasOpportunities, onReceiptsLoaded])

  if (!hasOpportunities) return null
  const total = (receipts || []).reduce((s, r) => s + (r.ReceiptTotalCost || 0), 0)

  // saved allocations keyed by receiptId -> { groupId: percent } (for the review window)
  const savedByReceiptId = (savedAllocations || []).reduce((m, a) => {
    m[a.receiptId] = (a.splits || []).reduce((mm, s) => { mm[String(s.groupId)] = s.percent; return mm }, {})
    return m
  }, {})
  // Save ONE receipt's splits: merge into the full set and PATCH wholesale.
  const saveOne = (receipt, splits) => {
    const others = (savedAllocations || []).filter(a => a.receiptId !== receipt.ReceiptID)
    const next = splits.length
      ? [...others, { receiptId: receipt.ReceiptID, receiptNumber: receipt.ReceiptNumber, splits }]
      : others
    onSaveAllocations(next)
  }

  return (
    <Card style={{ marginBottom: 16 }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            Purchase Receipts{receipts ? ` (${receipts.length} · ${money(total)})` : ''}
          </Typography>
          {receipts && !!receipts.length && (
            <Button size="small" variant="outlined" onClick={() => setReviewIdx(0)}>Review receipts</Button>
          )}
        </Box>
        {error && <Typography variant="body2" color="error">{error}</Typography>}
        {receipts === null && <Box mt={1}><CircularProgress size={20} /></Box>}
        {receipts && !receipts.length && !error && (
          <Typography variant="body2" color="textSecondary">No receipts found for the linked opportunities.</Typography>
        )}
        {receipts && !!receipts.length && (
          <AllocationMatrix
            groups={groups}
            rowColLabel="Receipt"
            heading="Attribute each receipt to invoice groups (%)"
            emptyRowsText="No receipts to attribute."
            rows={receipts.map(r => ({
              key: String(r.ReceiptID),
              receiptId: r.ReceiptID, receiptNumber: r.ReceiptNumber, receipt: r,
              primary: `#${r.ReceiptNumber || r.ReceiptID} · ${r.VendorName || `Vendor ${r.VendorID || '—'}`} · ${money(r.ReceiptTotalCost)}`,
              secondary: [
                `${(r.ReceiptItems || []).length} item${(r.ReceiptItems || []).length === 1 ? '' : 's'}`,
                r.VendorInvoiceNum && `inv ${r.VendorInvoiceNum}`,
                r.ReceiptStatusName, fmtDate(r.ReceivedDate)
              ].filter(Boolean).join(' · ')
            }))}
            savedByKey={(savedAllocations || []).reduce((m, a) => {
              m[String(a.receiptId)] = (a.splits || []).reduce((mm, s) => { mm[String(s.groupId)] = s.percent; return mm }, {})
              return m
            }, {})}
            buildAllocation={(r, splits) => ({ receiptId: r.receiptId, receiptNumber: r.receiptNumber, splits })}
            onSave={onSaveAllocations}
            rowAction={r => <Button size="small" onClick={() => setReviewIdx(receipts.findIndex(x => x.ReceiptID === r.receiptId))}>View</Button>}
          />
        )}
        {receipts && !!receipts.length && (
          <Box mt={2}>
            <Button size="small" onClick={() => setShowList(v => !v)}>
              {showList ? 'Hide' : 'Show'} {receipts.length} receipts
            </Button>
          </Box>
        )}
        {receipts && !!receipts.length && showList && (
          <List dense>
            {receipts.slice(0, 200).map(r => (
              <ListItem key={r.ReceiptID} disableGutters>
                <ListItemText
                  primary={`#${r.ReceiptNumber || r.ReceiptID} · ${r.VendorName || `Vendor ${r.VendorID || '—'}`} · ${money(r.ReceiptTotalCost)}${r.IsPurchaseCredit ? ' (credit)' : ''}`}
                  secondary={[
                    `${(r.ReceiptItems || []).length} item${(r.ReceiptItems || []).length === 1 ? '' : 's'}`,
                    r.VendorInvoiceNum && `inv ${r.VendorInvoiceNum}`,
                    `WT ${r.WorkTicketNumber || '—'}`, r.ReceiptStatusName, fmtDate(r.ReceivedDate)
                  ].filter(Boolean).join(' · ')}
                />
                <AspireLink href={aspireLink.receipt(r.ReceiptID)} label="Aspire" />
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>
      {reviewIdx != null && receipts && receipts[reviewIdx] && (
        <ReceiptReviewWindow
          url={url} draftId={draftId}
          receipts={receipts} startIndex={reviewIdx}
          groups={groups} savedByReceiptId={savedByReceiptId}
          onSaveOne={saveOne}
          onClose={() => setReviewIdx(null)}
        />
      )}
    </Card>
  )
}

/* ── grouping board (scoped to one draft) ──────────────────────── */
const Board = ({ url, draftId, onBack }) => {
  const [board, setBoard] = useState(null)
  const [error, setError] = useState(null)
  const [selected, setSelected] = useState({})       // lineItemId -> true
  const [menuAnchor, setMenuAnchor] = useState(null)
  const [newGroupOpen, setNewGroupOpen] = useState(false)
  const [newGroupName, setNewGroupName] = useState('')
  const [editingGroupId, setEditingGroupId] = useState(null)
  const [editingName, setEditingName] = useState('')
  const [importing, setImporting] = useState(false)
  const [editingPriceId, setEditingPriceId] = useState(null)  // lineItemId being priced
  const [priceDraft, setPriceDraft] = useState('')
  const [receipts, setReceipts] = useState([])  // reported up from ReceiptsPanel (for material math)
  const [crews, setCrews] = useState([])        // reported up from VisitsPanel (for labor math)
  const fileInputRef = useRef(null)

  const base = `${url}/api/bid-estimation/invoice-grouping`
  const load = useCallback(() => {
    axios.get(`${base}/drafts/${draftId}/board`)
      .then(res => { setBoard(res.data); setError(null) })
      .catch(e => setError(e.response?.data?.error || e.message))
  }, [base, draftId])
  useEffect(() => { load() }, [load])

  const itemById = useMemo(() => {
    const m = {}
    for (const li of board?.lineItems || []) m[li._id] = li
    return m
  }, [board])
  const groupedIds = useMemo(() => {
    const s = new Set()
    for (const g of board?.groups || []) for (const id of g.lineItemIds) s.add(id)
    return s
  }, [board])
  const ungrouped = useMemo(
    () => (board?.lineItems || []).filter(li => !groupedIds.has(li._id)),
    [board, groupedIds]
  )
  const selectedIds = Object.keys(selected).filter(id => selected[id])
  const sumSell = (items) => items.reduce((s, li) => s + effectiveTotal(li), 0)
  const grandTotal = sumSell(board?.lineItems || [])
  const groupedTotal = sumSell((board?.lineItems || []).filter(li => groupedIds.has(li._id)))

  // Material cost per group = Σ receipt.ReceiptTotalCost × (that receipt's % to the group).
  // Receipts are fetched by ReceiptsPanel and reported up via setReceipts.
  const materialByGroupId = useMemo(() => {
    const totalById = new Map((receipts || []).map(r => [r.ReceiptID, Number(r.ReceiptTotalCost) || 0]))
    const out = {}
    for (const a of board?.draft?.receiptAllocations || []) {
      const receiptTotal = totalById.get(a.receiptId)
      if (!receiptTotal) continue
      for (const s of a.splits || []) {
        const gid = String(s.groupId)
        out[gid] = (out[gid] || 0) + receiptTotal * (Number(s.percent) || 0) / 100
      }
    }
    return out
  }, [receipts, board])

  // Labor cost per group = Σ crew.laborCost × (that crew's % to the group).
  // Crews (with laborCost) are fetched by VisitsPanel and reported up via setCrews.
  const laborByGroupId = useMemo(() => {
    const costByRoute = new Map((crews || []).map(c => [c.routeId, Number(c.laborCost) || 0]))
    const out = {}
    for (const a of board?.draft?.allocations || []) {
      const crewCost = costByRoute.get(a.routeId)
      if (!crewCost) continue
      for (const s of a.splits || []) {
        const gid = String(s.groupId)
        out[gid] = (out[gid] || 0) + crewCost * (Number(s.percent) || 0) / 100
      }
    }
    return out
  }, [crews, board])

  // Draft-wide rollup across grouped items only (what will actually be invoiced).
  const rollup = useMemo(() => {
    const gids = (board?.groups || []).map(g => String(g._id))
    const material = gids.reduce((s, id) => s + (materialByGroupId[id] || 0), 0)
    const labor = gids.reduce((s, id) => s + (laborByGroupId[id] || 0), 0)
    const cost = material + labor
    return { sell: groupedTotal, material, labor, cost, gap: groupedTotal - cost }
  }, [board, materialByGroupId, laborByGroupId, groupedTotal])

  const refreshAfter = (promise) => promise.then(load).catch(e => {
    window.alert(e.response?.data?.error || e.message)
    load()
  })
  const addSelectedToGroup = (group) => {
    setMenuAnchor(null); setSelected({})
    refreshAfter(axios.patch(`${base}/groups/${group._id}`, {
      lineItemIds: [...group.lineItemIds, ...selectedIds]
    }))
  }
  const createGroup = () => {
    const name = newGroupName.trim()
    setNewGroupOpen(false); setNewGroupName(''); setSelected({})
    if (!name) return
    refreshAfter(axios.post(`${base}/drafts/${draftId}/groups`, { name, lineItemIds: selectedIds }))
  }
  const removeFromGroup = (group, lineItemId) =>
    refreshAfter(axios.patch(`${base}/groups/${group._id}`, {
      lineItemIds: group.lineItemIds.filter(id => id !== lineItemId)
    }))
  const renameGroup = (group) => {
    const name = editingName.trim()
    setEditingGroupId(null)
    if (name && name !== group.name) refreshAfter(axios.patch(`${base}/groups/${group._id}`, { name }))
  }
  const toggleReady = (group) =>
    refreshAfter(axios.patch(`${base}/groups/${group._id}`, { readyToInvoice: !group.readyToInvoice }))
  const saveOpportunities = (opps) =>
    refreshAfter(axios.patch(`${base}/drafts/${draftId}`, { opportunities: opps }))
  const saveAllocations = (allocations) =>
    refreshAfter(axios.patch(`${base}/drafts/${draftId}/allocations`, { allocations }))
  const saveHoursOverride = (hoursOverride) =>
    refreshAfter(axios.patch(`${base}/drafts/${draftId}`, { hoursOverride }))
  const saveCrewRates = (crewRates) =>
    refreshAfter(axios.patch(`${base}/drafts/${draftId}/crew-rates`, { crewRates }))
  const saveReceiptAllocations = (allocations) =>
    refreshAfter(axios.patch(`${base}/drafts/${draftId}/receipt-allocations`, { allocations }))
  const deleteGroup = (group) => {
    if (window.confirm(`Delete group "${group.name}"? Its items become ungrouped.`)) {
      refreshAfter(axios.delete(`${base}/groups/${group._id}`))
    }
  }
  const startEditPrice = (li) => {
    setEditingPriceId(li._id)
    setPriceDraft(li.importedTotal != null ? String(li.importedTotal) : '')
  }
  const savePrice = (li) => {
    const raw = priceDraft.trim()
    setEditingPriceId(null)
    const val = raw === '' ? null : Number(raw.replace(/[$,]/g, ''))
    if (raw !== '' && !Number.isFinite(val)) return
    if (val === (li.importedTotal ?? null)) return  // no change
    refreshAfter(axios.put(`${url}/api/bid-estimation/line-items/${li._id}`, { importedTotal: val }))
  }
  const importPrices = (file) => {
    if (!file) return
    setImporting(true)
    const form = new FormData()
    form.append('priceFile', file)
    axios.post(`${base}/projects/${board.project._id}/import-prices`, form)
      .then(res => {
        const { matched, parsed, unmatched = [] } = res.data || {}
        window.alert(
          `Imported prices for ${matched} of ${parsed} line item${parsed === 1 ? '' : 's'}.` +
          (unmatched.length ? `\nNo matching line item for item #: ${unmatched.join(', ')}.` : '')
        )
        load()
      })
      .catch(e => window.alert(e.response?.data?.error || e.message))
      .then(() => setImporting(false))
  }

  if (error) return <Box mt={6} textAlign="center"><Typography color="error">{error}</Typography><Button onClick={onBack}>Back to search</Button></Box>
  if (!board) return <Box mt={6} textAlign="center"><CircularProgress /></Box>

  return (
    <Box>
      {/* header */}
      <Box display="flex" alignItems="baseline" flexWrap="wrap" mb={2} style={{ gap: 16 }}>
        <Button size="small" onClick={onBack}>← Drafts</Button>
        <Box>
          <Typography variant="h5" style={{ lineHeight: 1.1 }}>{board.draft?.name || 'Invoice draft'}</Typography>
          <Typography variant="caption" color="textSecondary">{board.project.name}</Typography>
        </Box>
        <Chip size="small" label={`Grand total ${fmt(grandTotal)}`} />
        <Chip size="small" color="primary" label={`Grouped ${fmt(groupedTotal)}`} />
        <Chip size="small" variant="outlined" label={`Ungrouped ${fmt(grandTotal - groupedTotal)}`} />
        <Chip size="small" variant="outlined" label={`Cost ${fmt(rollup.cost)}`} style={{ color: '#4527a0' }} />
        <Chip size="small" label={`Gap ${fmt(rollup.gap)}${rollup.sell > 0 ? ` (${((rollup.gap / rollup.sell) * 100).toFixed(0)}%)` : ''}`}
          style={{ backgroundColor: rollup.gap < 0 ? '#c62828' : '#33691e', color: '#fff' }} />
        <Box flexGrow={1} />
        <Button
          size="small" variant="outlined" disabled={importing}
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
        >
          {importing ? 'Importing…' : 'Import prices (CSV/Excel)'}
        </Button>
        <input
          ref={fileInputRef} type="file" hidden
          accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          onChange={e => { const f = e.target.files[0]; e.target.value = ''; importPrices(f) }}
        />
      </Box>

      {/* Aspire jobs this invoice covers */}
      <Box mb={2}>
        <OpportunityPicker url={url} value={board.draft?.opportunities} onChange={saveOpportunities} />
      </Box>

      <VisitsPanel
        url={url} draftId={draftId}
        hasOpportunities={!!(board.draft?.opportunities || []).length}
        reloadKey={[
          (board.draft?.opportunities || []).map(o => o.OpportunityID).sort().join(','),
          `h:${board.draft?.hoursOverride ?? ''}`,
          `r:${(board.draft?.crewRates || []).map(r => `${r.routeId}=${r.rate}`).sort().join(',')}`
        ].join('|')}
        groups={board.groups || []}
        savedAllocations={board.draft?.allocations || []}
        onSaveAllocations={saveAllocations}
        onCrewsLoaded={setCrews}
        hoursOverride={board.draft?.hoursOverride}
        onSaveHoursOverride={saveHoursOverride}
        crewRates={board.draft?.crewRates || []}
        onSaveCrewRates={saveCrewRates}
      />

      <ReceiptsPanel
        url={url} draftId={draftId}
        hasOpportunities={!!(board.draft?.opportunities || []).length}
        reloadKey={(board.draft?.opportunities || []).map(o => o.OpportunityID).sort().join(',')}
        groups={board.groups || []}
        savedAllocations={board.draft?.receiptAllocations || []}
        onSaveAllocations={saveReceiptAllocations}
        onReceiptsLoaded={setReceipts}
      />

      <Box display="flex" alignItems="flex-start" style={{ gap: 16 }}>
        {/* left: ungrouped items */}
        <Card style={{ flex: 5, minWidth: 0 }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="h6">Ungrouped line items ({ungrouped.length})</Typography>
              <Box>
                <Button size="small" disabled={!selectedIds.length} onClick={e => setMenuAnchor(e.currentTarget)}>
                  Add {selectedIds.length ? `${selectedIds.length} ` : ''}to group…
                </Button>
              </Box>
            </Box>
            <List dense>
              {ungrouped.map(li => (
                <ListItem key={li._id} button onClick={() => setSelected(s => ({ ...s, [li._id]: !s[li._id] }))}>
                  <ListItemIcon><Checkbox edge="start" size="small" checked={!!selected[li._id]} tabIndex={-1} disableRipple /></ListItemIcon>
                  <ListItemText
                    primary={`${li.itemNo != null ? `#${li.itemNo} — ` : ''}${li.description}`}
                    secondaryTypographyProps={{ component: 'div' }}
                    secondary={
                      <Box component="span" display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 4 }}>
                        <span>{`${li.quantity || 0} ${li.unit || ''} · cost ${fmt(li.totalCost)} · sell `}</span>
                        {editingPriceId === li._id ? (
                          <TextField
                            size="small" autoFocus value={priceDraft}
                            placeholder="0.00"
                            onClick={e => e.stopPropagation()}
                            onChange={e => setPriceDraft(e.target.value)}
                            onBlur={() => savePrice(li)}
                            onKeyDown={e => { if (e.key === 'Enter') savePrice(li); if (e.key === 'Escape') setEditingPriceId(null) }}
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                            style={{ width: 130 }}
                          />
                        ) : (
                          <span
                            role="button"
                            onClick={e => { e.stopPropagation(); startEditPrice(li) }}
                            style={{ cursor: 'pointer', textDecoration: 'underline dotted', fontWeight: 500 }}
                          >
                            {effectiveTotal(li) > 0 ? `${fmt(effectiveTotal(li))}${isImported(li) ? ' (entered)' : ''}` : 'set price'}
                          </span>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
              {!ungrouped.length && <Typography variant="body2" color="textSecondary">Everything is grouped. 🎉</Typography>}
            </List>
          </CardContent>
        </Card>

        {/* right: groups */}
        <Box style={{ flex: 4, minWidth: 0 }}>
          <Button fullWidth variant="outlined" startIcon={<AddIcon />} onClick={() => setNewGroupOpen(true)} style={{ marginBottom: 12 }}>
            New invoice group{selectedIds.length ? ` (with ${selectedIds.length} selected)` : ''}
          </Button>
          {(board.groups || []).map(g => {
            const items = g.lineItemIds.map(id => itemById[id]).filter(Boolean)
            const sell = sumSell(items)
            const attribCost = (materialByGroupId[String(g._id)] || 0) + (laborByGroupId[String(g._id)] || 0)
            const gap = sell - attribCost
            const marginPct = sell > 0 ? (gap / sell) * 100 : null
            return (
              <Card key={g._id} style={{ marginBottom: 12, border: g.readyToInvoice ? '1px solid #4caf50' : undefined }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    {editingGroupId === g._id ? (
                      <TextField
                        size="small" value={editingName} autoFocus
                        onChange={e => setEditingName(e.target.value)}
                        onBlur={() => renameGroup(g)}
                        onKeyDown={e => e.key === 'Enter' && renameGroup(g)}
                      />
                    ) : (
                      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <Typography variant="h6" style={{ cursor: 'pointer' }}
                          onClick={() => { setEditingGroupId(g._id); setEditingName(g.name) }}>
                          {g.name}
                        </Typography>
                        {g.readyToInvoice && <Chip size="small" label="Ready to Invoice" style={{ backgroundColor: '#4caf50', color: '#fff' }} />}
                      </Box>
                    )}
                    <Box display="flex" alignItems="center">
                      <Typography variant="subtitle1" style={{ marginRight: 8 }}>{fmt(sumSell(items))}</Typography>
                      <IconButton size="small" onClick={() => deleteGroup(g)}><DeleteIcon fontSize="small" /></IconButton>
                    </Box>
                  </Box>
                  <FormControlLabel
                    control={<Switch size="small" color="primary" checked={!!g.readyToInvoice} onChange={() => toggleReady(g)} />}
                    label={<Typography variant="caption">Ready to Invoice</Typography>}
                    style={{ marginLeft: 0 }}
                  />
                  <Box display="flex" flexWrap="wrap" alignItems="baseline" style={{ gap: 12 }}>
                    <Typography variant="caption" style={{ color: '#1565c0', fontWeight: 600 }}>
                      Material {fmt(materialByGroupId[String(g._id)] || 0)}
                    </Typography>
                    <Typography variant="caption" style={{ color: '#6a1b9a', fontWeight: 600 }}>
                      Labor {fmt(laborByGroupId[String(g._id)] || 0)}
                    </Typography>
                  </Box>
                  {/* Delta: sell − (material+labor). Gap = markup + still-missing costs
                      (drive time, QA/supervisor, equipment). Negative = cost exceeds sell. */}
                  <Box mt={0.5} py={0.5} px={1} style={{ background: gap < 0 ? '#fdecea' : '#f1f8e9', borderRadius: 4, display: 'inline-block' }}>
                    <Typography variant="caption" style={{ fontWeight: 600, color: gap < 0 ? '#c62828' : '#33691e' }}>
                      Gap {fmt(gap)}{marginPct != null ? ` (${marginPct.toFixed(0)}% margin)` : ''}
                      {' '}= sell {fmt(sell)} − cost {fmt(attribCost)}
                    </Typography>
                  </Box>
                  <List dense>
                    {items.map(li => (
                      <ListItem key={li._id} disableGutters>
                        <ListItemText
                          primary={`${li.itemNo != null ? `#${li.itemNo} — ` : ''}${li.description}`}
                          secondary={`${li.quantity || 0} ${li.unit || ''} · ${fmt(effectiveTotal(li))}${isImported(li) ? ' (entered)' : ''}`}
                        />
                        <IconButton size="small" onClick={() => removeFromGroup(g, li._id)}><CloseIcon fontSize="small" /></IconButton>
                      </ListItem>
                    ))}
                    {!items.length && <Typography variant="body2" color="textSecondary">Empty — add items from the left.</Typography>}
                  </List>
                </CardContent>
              </Card>
            )
          })}
          {!(board.groups || []).length && (
            <Typography variant="body2" color="textSecondary" align="center">
              No invoice groups yet. Select items on the left, then create one.
            </Typography>
          )}
        </Box>
      </Box>

      {/* add-to-group menu */}
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        {(board.groups || []).map(g => <MenuItem key={g._id} onClick={() => addSelectedToGroup(g)}>{g.name}</MenuItem>)}
        <MenuItem onClick={() => { setMenuAnchor(null); setNewGroupOpen(true) }}><AddIcon fontSize="small" />&nbsp;New group…</MenuItem>
      </Menu>

      {/* new group dialog */}
      <Dialog open={newGroupOpen} onClose={() => setNewGroupOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>New invoice group</DialogTitle>
        <DialogContent>
          <TextField fullWidth autoFocus label="Group name" value={newGroupName}
            onChange={e => setNewGroupName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && createGroup()} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewGroupOpen(false)}>Cancel</Button>
          <Button color="primary" variant="contained" disabled={!newGroupName.trim()} onClick={createGroup}>Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

/* ── page shell: draft id lives in ?draftId= so refresh restores it ── */
const InvoiceGroupingPage = ({ url, history, location }) => {
  const draftId = new URLSearchParams(location.search).get('draftId')
  const setDraftId = (id) =>
    history.replace(id ? `${location.pathname}?draftId=${id}` : location.pathname)
  return (
    <Container maxWidth="xl" style={{ paddingTop: 24, paddingBottom: 48 }}>
      {draftId
        ? <Board url={url} draftId={draftId} onBack={() => setDraftId(null)} />
        : <DraftList url={url} onOpen={setDraftId} />}
    </Container>
  )
}

const mapStateToProps = (state) => ({ url: state.url })
export default connect(mapStateToProps)(InvoiceGroupingPage)
